/**
 * My — the «Моё» tab: the listener's favourites and the tracks they played lately.
 *
 * Task 11. `spec.md` stories 57–59 and «Решение»: «„Моё“ (избранное, недавние)». Two sections
 * behind one switch, in that order, drawn like the sections of «Треки»: the same switch, the
 * same rows with their hearts, the same list that pages as it scrolls
 * (`components/TrackListView.tsx`). Both lists come from the server (`apps/api/src/routes/my.ts`),
 * so another phone of the same Telegram account shows the same two.
 *
 * ## Each entry asks the server again, and nothing moves while you look
 *
 * A list is fetched afresh every time the section appears — a heart put on in «Треки» or a
 * track heard a minute ago is there the next time the tab is opened. A section left is
 * forgotten (`forgetList`), so the next entry says «Загружаю…» until the server answers
 * instead of drawing the rows of last time and swapping them a moment later: a swap right
 * after entry is a row moving under the thumb too.
 *
 * While the section is on screen, it stays as it was drawn. A heart taken off here leaves its
 * row in place with an empty heart, and a track started here does not jump to the top of
 * «Недавние». A row that vanished or moved under the thumb would put the next tap on a
 * different track; a row that stays can take its heart back with a second tap. The next entry
 * shows the list as the server has it.
 *
 * ## An empty section shows a neon sign (task 68)
 *
 * No favourites yet, nothing heard yet, nothing new this week: a neon sign of its own — a heart,
 * headphones, an hourglass (`components/NeonSign.tsx`) — over the sentence that says what will
 * gather here (`ListState`). A load or a failure has none. Until task 68 it was the dimmed logo.
 *
 * ## «Новое» — the week of the channel (task 51)
 *
 * The third section: the tracks posted in the last seven days, newest first —
 * `GET /api/tracks?since=<unix>` — what the Friday digest posts to the channel, inside the app
 * (`spec.md` stories 147–148). The week is counted from the moment the tab is opened, to the
 * minute; the list is asked afresh on every entry and forgotten on leaving, like the other two.
 * Empty, it says so honestly, under the sign «Скоро», with «Смотреть все треки» under it — the
 * shell opens «Треки» on «Все треки».
 *
 * ## How many in each, on the switch (task 72)
 *
 * «Избранное 6», «Недавние 12», «Новое 5»: on every entry the screen asks the server how long each
 * of the three lists is — one row each, for the `total` a first page carries
 * (`lib/api.ts`, `fetchListTotal`). A number not known yet is not drawn. It is for the eye: the
 * buttons are still named by their words, and a list's own length is said by its rows.
 *
 * ## A tap plays, «⋮» opens the card
 *
 * As everywhere: the row plays with this list from that row down as the queue, and the card's
 * artist, riddim and tags open their lists in «Треки» — the shell switches the tab.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { ScreenHead } from '../components/ScreenHead';
import type { NeonKind } from '../components/NeonSign';
import { TrackCard } from '../components/TrackCard';
import { TrackListView, playIn } from '../components/TrackListView';
import { player } from '../engine/player';
import { fetchListTotal } from '../lib/api';
import type { TrackItem, TrackQuery } from '../lib/api';
import { useFavorites } from '../store/favorites';
import { useSession } from '../store/session';
import { listKey, useLibrary } from '../store/library';
import type { Collection, MySection } from '../store/library';
import '../styles/tracks.css';

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ──────────────────────────────────────────────────────────────────────────── */

export const MY_TITLE = 'Моё';
export const FAVORITES_LABEL = 'Избранное';
export const RECENT_LABEL = 'Недавние';
/** Task 51. */
export const FRESH_LABEL = 'Новое';
export const EMPTY_FRESH_TEXT = 'За неделю новых треков не было.';
/** Task 51: the length of «Новое», like the weekly digest's. */
export const FRESH_DAYS = 7;
export const MY_LOADING_TEXT = 'Загружаю…';
export const EMPTY_FAVORITES_TEXT =
  'Отмечайте треки сердечком — в списке, в плеере или на экране с пластинкой. Они соберутся здесь.';
export const EMPTY_RECENT_TEXT = 'Здесь появятся треки, которые вы слушали.';
/** Task 68: under an empty «Новое». */
export const ALL_TRACKS_LABEL = 'Смотреть все треки';

/** One section: its name on the switch and on the list, what it asks for, what it says empty. */
interface SectionView {
  readonly id: MySection;
  readonly label: string;
  /**
   * One object for the life of the app — see `TrackListView`. «Новое» has none here: its query
   * names a moment, and the screen makes one when it opens.
   */
  readonly query: TrackQuery | undefined;
  readonly emptyText: string;
  /** Task 68: the neon sign of the section when it is empty. */
  readonly sign: NeonKind;
}

/** Task 51: seven days back from now, to the minute — one moment for the screen's life. */
export function weekAgo(nowMs: number = Date.now()): number {
  const nowS = Math.floor(nowMs / 1000);
  return nowS - (nowS % 60) - FRESH_DAYS * 24 * 60 * 60;
}

const VIEWS: Readonly<Record<MySection, SectionView>> = Object.freeze({
  favorites: {
    id: 'favorites',
    label: FAVORITES_LABEL,
    query: Object.freeze({ kind: 'favorites' }),
    emptyText: EMPTY_FAVORITES_TEXT,
    sign: 'izbrannoe',
  },
  recent: {
    id: 'recent',
    label: RECENT_LABEL,
    query: Object.freeze({ kind: 'recent' }),
    emptyText: EMPTY_RECENT_TEXT,
    sign: 'nedavnie',
  },
  fresh: {
    id: 'fresh',
    label: FRESH_LABEL,
    query: undefined,
    emptyText: EMPTY_FRESH_TEXT,
    sign: 'novoe',
  },
});

/** The switch, in the order the specification names the sections; «Новое» last (task 51). */
export const MY_SECTIONS: readonly SectionView[] = Object.freeze([
  VIEWS.favorites,
  VIEWS.recent,
  VIEWS.fresh,
]);

/**
 * Asked afresh on every entry: see «Each entry asks the server again». A section left is
 * forgotten, so an entry usually finds nothing cached anyway; this covers the answer that
 * lands after the section was left, which is cached again — zero, because nothing about these
 * lists can be known from how recently they were read: only the server has the taps of
 * another phone.
 */
const ALWAYS = 0;

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ──────────────────────────────────────────────────────────────────────────── */

export interface MyScreenProps {
  /** Opens an artist's, a riddim's or a tag's list in «Треки». The shell switches the tab. */
  readonly onOpenCollection: (collection: Collection) => void;
  /** Task 68: «Смотреть все треки» under an empty «Новое» — «Треки» on «Все треки». */
  readonly onOpenAllTracks?: (() => void) | undefined;
}

export function MyScreen({ onOpenCollection, onOpenAllTracks }: MyScreenProps): JSX.Element {
  const section = useLibrary((s) => s.mySection);
  const showMySection = useLibrary((s) => s.showMySection);
  const [card, setCard] = useState<TrackItem | undefined>(undefined);
  // Task 51: one week for the screen's life — the query keeps its identity while it shows.
  const [freshQuery] = useState<TrackQuery>(() =>
    Object.freeze({ kind: 'fresh', since: weekAgo() }),
  );
  const view = VIEWS[section];
  const query = view.query ?? freshQuery;

  // Task 72: the three lengths, asked afresh on every entry to the tab.
  const [counts, setCounts] = useState<Partial<Record<MySection, number>>>({});
  useEffect(() => {
    let alive = true;
    const token = useSession.getState().session?.token;
    for (const id of ['favorites', 'recent', 'fresh'] as const) {
      void fetchListTotal(token, VIEWS[id].query ?? freshQuery).then((result) => {
        const total = result.ok ? result.data : undefined;
        if (alive && total !== undefined) setCounts((before) => ({ ...before, [id]: total }));
      });
    }
    return () => {
      alive = false;
    };
  }, [freshQuery]);

  // The hearts of the server are read at launch (`app/Shell.tsx`); if that did not work, the
  // tab where they matter most asks once more.
  useEffect(() => {
    if (!useFavorites.getState().loaded) void useFavorites.getState().load();
  }, []);

  // A section left is forgotten — see «Each entry asks the server again».
  useEffect(
    () => () => {
      useLibrary.getState().forgetList(query);
    },
    [query],
  );

  const closeCard = useCallback((): void => {
    setCard(undefined);
  }, []);
  const playFromCard = useCallback(
    (track: TrackItem): void => {
      playIn(listKey(query), track);
    },
    [query],
  );
  const enqueue = useCallback((track: TrackItem): boolean => player().enqueue(track), []);
  const openFromCard = useCallback(
    (collection: Collection): void => {
      setCard(undefined);
      onOpenCollection(collection);
    },
    [onOpenCollection],
  );

  return (
    <div className="tracks">
      <header className="tracksHead">
        <ScreenHead title={MY_TITLE} />
        <div className="segments" role="group" aria-label="Разделы «Моё»">
          {MY_SECTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              className="segment"
              aria-pressed={item.id === section}
              onClick={() => {
                showMySection(item.id);
              }}
            >
              {item.label}
              {counts[item.id] !== undefined && (
                <span className="segmentCount" aria-hidden="true">
                  {counts[item.id]}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <TrackListView
        key={section}
        query={query}
        label={view.label}
        emptyText={view.emptyText}
        loadingText={MY_LOADING_TEXT}
        maxAgeMs={ALWAYS}
        onOpenTrack={setCard}
        emptySign={view.sign}
        emptyAction={
          section === 'fresh' && onOpenAllTracks !== undefined ? (
            <button type="button" className="button buttonSecondary" onClick={onOpenAllTracks}>
              {ALL_TRACKS_LABEL}
            </button>
          ) : undefined
        }
      />

      {card !== undefined && (
        <TrackCard
          track={card}
          onClose={closeCard}
          onOpenCollection={openFromCard}
          onPlay={playFromCard}
          onEnqueue={enqueue}
        />
      )}
    </div>
  );
}
